import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft, Activity, Webhook, Eye, AlertCircle, Database, ShieldCheck } from "lucide-react";
import { adminCsrfHeaders } from "@/lib/adminApi";

interface JobSummary {
  jobName: string;
  status: string;
  durationMs: number | null;
  error: string | null;
  lastStartedAt: string;
  lastFinishedAt: string | null;
}

interface ProviderSummary {
  processed: number;
  errored: number;
  received?: number;
  lastError: string | null;
  lastErrorAt: string | null;
  eventTypes?: Record<string, number>;
}

interface HealthData {
  migrations?: { applied: number; onDisk: number; pending: string[] };
  jobs?: JobSummary[];
  webhookProviders?: Record<string, ProviderSummary> | Array<Record<string, unknown>>;
  schedulerRuns: { id: number; jobName: string; status: string; durationMs: number | null; error: string | null; startedAt: string; finishedAt: string | null }[];
  webhookEvents: { id: number; provider: string; eventType: string; externalId: string | null; status: string; error: string | null; receivedAt: string; processedAt: string | null }[];
  activeImpersonations: number;
}

interface LegacyScanStatus {
  contractDocuments: { legacyPending: number; scannerPending: number; clean: number; infected: number; error: number };
  objectsReferenced: number;
  objectsCached: number;
  objectsPending: number;
}

interface LegacyScanReport {
  contractDocuments: { scanned: number; clean: number; infected: number; error: number; missing: number };
  objects: { scanned: number; clean: number; infected: number; error: number; missing: number };
  startedAt: string;
  finishedAt: string;
  durationMs: number;
}

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { dateStyle: "short", timeStyle: "medium" });
}

export default function AdminHealth() {
  const [, setLocation] = useLocation();
  const [data, setData] = useState<HealthData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [legacyScan, setLegacyScan] = useState<LegacyScanStatus | null>(null);
  const [legacyReport, setLegacyReport] = useState<LegacyScanReport | null>(null);
  const [legacyRunning, setLegacyRunning] = useState(false);
  const [legacyError, setLegacyError] = useState<string | null>(null);

  const refreshLegacyStatus = async () => {
    try {
      const res = await fetch("/api/admin/backfill-malware-scans/status", { credentials: "include" });
      if (res.ok) setLegacyScan(await res.json() as LegacyScanStatus);
    } catch { /* best-effort */ }
  };

  const runLegacyScan = async () => {
    setLegacyRunning(true);
    setLegacyError(null);
    try {
      const res = await fetch("/api/admin/backfill-malware-scans", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...adminCsrfHeaders() },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        setLegacyError(`Backfill failed (HTTP ${res.status})`);
      } else {
        setLegacyReport(await res.json() as LegacyScanReport);
        await refreshLegacyStatus();
      }
    } catch (err) {
      setLegacyError((err as Error).message);
    } finally {
      setLegacyRunning(false);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch("/api/admin/health", { credentials: "include" });
        if (res.status === 401) { setLocation("/admin/login"); return; }
        if (!res.ok) { setError("Failed to load health data."); return; }
        setData(await res.json() as HealthData);
        await refreshLegacyStatus();
      } catch { setError("Failed to load health data."); }
    })();
  }, [setLocation]);

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Link href="/admin" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to console
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <Activity className="h-6 w-6 text-blue-400" />
          <h1 className="text-2xl font-bold">Platform Health</h1>
        </div>

        {error && (
          <div className="text-center py-12">
            <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-3" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-3">
                <Eye className="h-5 w-5 text-yellow-400" />
                <div>
                  <p className="text-xs text-gray-500">Active impersonations</p>
                  <p className="text-lg font-semibold">{data.activeImpersonations}</p>
                </div>
              </div>
              {data.migrations && (
                <div className={`bg-gray-900 border rounded-xl p-4 flex items-center gap-3 ${data.migrations.pending.length > 0 ? "border-red-700/60" : "border-gray-800"}`}>
                  <Database className={`h-5 w-5 ${data.migrations.pending.length > 0 ? "text-red-400" : "text-green-400"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Migrations</p>
                    <p className="text-sm">
                      <span className="font-semibold">{data.migrations.applied}</span>
                      <span className="text-gray-500"> / {data.migrations.onDisk} applied</span>
                    </p>
                    {data.migrations.pending.length > 0 && (
                      <p className="text-xs text-red-300 mt-1 truncate" title={data.migrations.pending.join(", ")}>
                        {data.migrations.pending.length} pending: {data.migrations.pending.slice(0, 2).join(", ")}
                        {data.migrations.pending.length > 2 ? "…" : ""}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                <h2 className="text-sm font-semibold">Legacy upload re-scan (Task #426)</h2>
              </div>
              <div className="px-5 py-4 space-y-3 text-xs">
                {legacyScan ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <p className="text-gray-500">Contract docs unscanned</p>
                      <p className={`text-lg font-semibold ${legacyScan.contractDocuments.legacyPending > 0 ? "text-yellow-300" : "text-green-400"}`}>{legacyScan.contractDocuments.legacyPending}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Contract docs infected</p>
                      <p className={`text-lg font-semibold ${legacyScan.contractDocuments.infected > 0 ? "text-red-400" : "text-gray-300"}`}>{legacyScan.contractDocuments.infected}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Referenced storage keys</p>
                      <p className="text-lg font-semibold text-gray-300">{legacyScan.objectsCached} / {legacyScan.objectsReferenced} scanned</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Storage keys to scan</p>
                      <p className={`text-lg font-semibold ${legacyScan.objectsPending > 0 ? "text-yellow-300" : "text-green-400"}`}>{legacyScan.objectsPending}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">Loading legacy scan status…</p>
                )}
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={() => void runLegacyScan()}
                    disabled={legacyRunning}
                    className="px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold"
                  >
                    {legacyRunning ? "Scanning…" : "Run re-scan pass (≤500 / class)"}
                  </button>
                  <span className="text-gray-500">Safe to re-run; rows already labelled are skipped.</span>
                </div>
                {legacyError && (
                  <p className="text-red-400">{legacyError}</p>
                )}
                {legacyReport && (
                  <div className="border-t border-gray-800 pt-3 mt-2 space-y-1">
                    <p className="text-gray-300 font-semibold">
                      Last run: {fmt(legacyReport.startedAt)} · {legacyReport.durationMs}ms
                    </p>
                    <p>
                      <span className="text-gray-500">Contract docs:</span>{" "}
                      <span className="text-gray-200">{legacyReport.contractDocuments.scanned} scanned</span>
                      {" — "}
                      <span className="text-green-400">{legacyReport.contractDocuments.clean} clean</span>{", "}
                      <span className={legacyReport.contractDocuments.infected > 0 ? "text-red-400" : "text-gray-400"}>{legacyReport.contractDocuments.infected} infected</span>{", "}
                      <span className={legacyReport.contractDocuments.error > 0 ? "text-yellow-300" : "text-gray-400"}>{legacyReport.contractDocuments.error} error</span>{", "}
                      <span className="text-gray-400">{legacyReport.contractDocuments.missing} missing</span>
                    </p>
                    <p>
                      <span className="text-gray-500">Referenced objects:</span>{" "}
                      <span className="text-gray-200">{legacyReport.objects.scanned} scanned</span>
                      {" — "}
                      <span className="text-green-400">{legacyReport.objects.clean} clean</span>{", "}
                      <span className={legacyReport.objects.infected > 0 ? "text-red-400" : "text-gray-400"}>{legacyReport.objects.infected} infected</span>{", "}
                      <span className={legacyReport.objects.error > 0 ? "text-yellow-300" : "text-gray-400"}>{legacyReport.objects.error} error</span>{", "}
                      <span className="text-gray-400">{legacyReport.objects.missing} missing</span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {data.jobs && data.jobs.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
                  <Activity className="h-4 w-4" /><h2 className="text-sm font-semibold">Job summary (latest run per job)</h2>
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-gray-800">{["Job", "Status", "Last started", "Last finished", "Duration", "Last error"].map((h) => <th key={h} className="text-left px-4 py-2 text-gray-500">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.jobs.map((j) => (
                      <tr key={j.jobName} className="border-b border-gray-800/50">
                        <td className="px-4 py-1.5 text-gray-200">{j.jobName}</td>
                        <td className={`px-4 py-1.5 ${j.status === "success" ? "text-green-400" : j.status === "error" ? "text-red-400" : "text-yellow-400"}`}>{j.status}</td>
                        <td className="px-4 py-1.5 text-gray-400">{fmt(j.lastStartedAt)}</td>
                        <td className="px-4 py-1.5 text-gray-400">{fmt(j.lastFinishedAt)}</td>
                        <td className="px-4 py-1.5 text-gray-400">{j.durationMs != null ? `${j.durationMs}ms` : "—"}</td>
                        <td className="px-4 py-1.5 text-red-400 max-w-xs truncate">{j.error ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.webhookProviders && !Array.isArray(data.webhookProviders) && Object.keys(data.webhookProviders).length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
                  <Webhook className="h-4 w-4" /><h2 className="text-sm font-semibold">Webhook providers (last 24h)</h2>
                </div>
                <div className="divide-y divide-gray-800">
                  {Object.entries(data.webhookProviders as Record<string, ProviderSummary>).map(([prov, p]) => (
                    <div key={prov} className="px-5 py-3 text-xs">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-gray-200 font-semibold capitalize">{prov}</span>
                        <span className="text-green-400">processed: {p.processed}</span>
                        <span className={p.errored > 0 ? "text-red-400" : "text-gray-500"}>errored: {p.errored}</span>
                        {typeof p.received === "number" && (
                          <span className="text-gray-500">received: {p.received}</span>
                        )}
                      </div>
                      {p.eventTypes && Object.keys(p.eventTypes).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {Object.entries(p.eventTypes).map(([t, c]) => {
                            const bad = ["bounced", "complained", "failed", "spam", "rejected"].some((b) => t.toLowerCase().includes(b));
                            return (
                              <span
                                key={t}
                                className={`px-1.5 py-0.5 rounded ${bad ? "bg-red-900/40 text-red-300" : "bg-gray-800 text-gray-300"}`}
                              >
                                {t}: {c}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {p.lastError && (
                        <p className="mt-1.5 text-red-400 truncate" title={p.lastError}>
                          last error ({fmt(p.lastErrorAt)}): {p.lastError}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
                <Activity className="h-4 w-4" /><h2 className="text-sm font-semibold">Scheduler runs (last 50)</h2>
              </div>
              {data.schedulerRuns.length === 0 ? (
                <p className="px-5 py-3 text-sm text-gray-500">No runs recorded yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-gray-800">{["Job", "Status", "Started", "Finished", "Duration", "Error"].map((h) => <th key={h} className="text-left px-4 py-2 text-gray-500">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.schedulerRuns.map((r) => (
                      <tr key={r.id} className="border-b border-gray-800/50">
                        <td className="px-4 py-1.5 text-gray-200">{r.jobName}</td>
                        <td className={`px-4 py-1.5 ${r.status === "success" ? "text-green-400" : r.status === "error" ? "text-red-400" : "text-yellow-400"}`}>{r.status}</td>
                        <td className="px-4 py-1.5 text-gray-400">{fmt(r.startedAt)}</td>
                        <td className="px-4 py-1.5 text-gray-400">{fmt(r.finishedAt)}</td>
                        <td className="px-4 py-1.5 text-gray-400">{r.durationMs != null ? `${r.durationMs}ms` : "—"}</td>
                        <td className="px-4 py-1.5 text-red-400 max-w-xs truncate">{r.error ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
                <Webhook className="h-4 w-4" /><h2 className="text-sm font-semibold">Webhook events (last 50)</h2>
              </div>
              {data.webhookEvents.length === 0 ? (
                <p className="px-5 py-3 text-sm text-gray-500">No webhook events recorded yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-gray-800">{["Provider", "Event", "External ID", "Status", "Received", "Error"].map((h) => <th key={h} className="text-left px-4 py-2 text-gray-500">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.webhookEvents.map((e) => (
                      <tr key={e.id} className="border-b border-gray-800/50">
                        <td className="px-4 py-1.5 text-gray-200">{e.provider}</td>
                        <td className="px-4 py-1.5 text-gray-300">{e.eventType}</td>
                        <td className="px-4 py-1.5 text-gray-500 max-w-xs truncate">{e.externalId ?? "—"}</td>
                        <td className={`px-4 py-1.5 ${e.status === "processed" ? "text-green-400" : e.status === "error" ? "text-red-400" : "text-gray-400"}`}>{e.status}</td>
                        <td className="px-4 py-1.5 text-gray-400">{fmt(e.receivedAt)}</td>
                        <td className="px-4 py-1.5 text-red-400 max-w-xs truncate">{e.error ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
